import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Plus, Pencil, Trash2, Search, X, Package } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* ─── helpers ─── */
const fmt = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/* ─── borda vertical entre células ─── */
const VBORDER = '1px solid #1a2035';

/* ─── estilos ─── */
const S = {
  page:    { maxWidth: 1140, margin: '0 auto' },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  title:   { fontSize: 22, fontWeight: 700, color: '#f6c90e' },
  sub:     { fontSize: 13, color: '#475569', marginTop: 2 },

  filterBar:   { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 10, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' },
  filterLabel: { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 5 },
  filterInput: { background: '#080c14', border: '1px solid #1a2035', borderRadius: 7, padding: '8px 12px', color: '#e2e8f0', fontSize: 13, width: 130, outline: 'none' },

  btnPrimary: { background: '#f6c90e', color: '#080c14', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  btnGhost:   { background: 'transparent', color: '#64748b', border: '1px solid #1a2035', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  btnDanger:  { background: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 7, padding: '6px 10px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 },
  btnEdit:    { background: 'transparent', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)', borderRadius: 7, padding: '6px 10px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 },

  card:  { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 12, overflowX: 'auto', overflowY: 'auto', maxHeight: 400 },
  table: { width: '100%', borderCollapse: 'collapse' },

  th: (right) => ({
    padding: '11px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderBottom: '1px solid #1a2035',
    borderRight: VBORDER,
    textAlign: right ? 'right' : 'left',
    background: '#0b111e',
    whiteSpace: 'nowrap',
  }),
  thLast: {
    padding: '11px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderBottom: '1px solid #1a2035',
    textAlign: 'right',
    background: '#0b111e',
  },

  td: (right) => ({
    padding: '12px 16px',
    fontSize: 13,
    color: '#cbd5e1',
    borderBottom: '1px solid #0d1525',
    borderRight: VBORDER,
    textAlign: right ? 'right' : 'left',
    verticalAlign: 'middle',
  }),
  tdLast: {
    padding: '8px 12px',
    borderBottom: '1px solid #0d1525',
    textAlign: 'right',
    verticalAlign: 'middle',
  },

  tr: (i) => ({ background: i % 2 === 0 ? '#080c14' : '#090e1c' }),

  badge: { display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: 'rgba(246,201,14,0.12)', color: '#f6c90e', border: '1px solid rgba(246,201,14,0.2)' },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:   { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 12, width: 480, padding: 28, maxHeight: '90vh', overflowY: 'auto' },
  mTitle:  { fontSize: 16, fontWeight: 700, color: '#f6c90e', marginBottom: 20 },
  label:   { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, display: 'block', marginBottom: 5 },
  input:   { width: '100%', background: '#080c14', border: '1px solid #1a2035', borderRadius: 7, padding: '9px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  row2:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  fgroup:  { marginBottom: 16 },
  mFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 },

  empty: { textAlign: 'center', padding: '60px 0', color: '#475569', fontSize: 14 },
};

const EMPTY_FORM = { geracao: '', placas_w: '', quantidade: '', qtd_inversores: '', equipamento: '', garantia_inversor: '', marca: '', valor: '' };

/* ─── Modal de Confirmação ─── */
function ConfirmModal({ msg, onConfirm, onCancel }) {
  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, width: 360 }}>
        <div style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 20 }}>{msg}</div>
        <div style={S.mFooter}>
          <button style={S.btnGhost} onClick={onCancel}>Cancelar</button>
          <button style={{ ...S.btnPrimary, background: '#ef4444', color: '#fff' }} onClick={onConfirm}>Deletar</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Modal de Criação / Edição ─── */
function KitModal({ kit, onSave, onClose }) {
  const { isChefe, isGerenteComercial } = useAuth();
  const [form, setForm] = useState(kit ? {
    geracao:           String(kit.geracao),
    placas_w:          String(kit.placas_w),
    quantidade:        String(kit.quantidade),
    qtd_inversores:    kit.qtd_inversores != null ? String(kit.qtd_inversores) : '',
    equipamento:       kit.equipamento,
    garantia_inversor: kit.garantia_inversor != null ? String(kit.garantia_inversor) : '',
    marca:             kit.marca || '',
    valor:             String(kit.valor),
  } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.geracao || !form.placas_w || !form.quantidade || !form.equipamento || !form.valor) {
      setErr('Preencha todos os campos.'); return;
    }
    setSaving(true);
    try {
      const payload = {
        geracao:        parseInt(form.geracao),
        placas_w:       parseInt(form.placas_w),
        quantidade:     parseInt(form.quantidade),
        qtd_inversores: form.qtd_inversores ? parseInt(form.qtd_inversores) : null,
        equipamento:    form.equipamento.trim(),
        marca:          form.marca.trim(),
        valor:          parseFloat(String(form.valor).replace(',', '.')),
      };
      if (isChefe() || isGerenteComercial()) {
        payload.garantia_inversor = form.garantia_inversor ? parseInt(form.garantia_inversor) : null;
      }
      if (kit) {
        const r = await api.patch(`/kits/${kit.id}/`, payload);
        onSave(r.data, 'edit');
      } else {
        const r = await api.post('/kits/', payload);
        onSave(r.data, 'create');
      }
    } catch {
      setErr('Erro ao salvar. Verifique os dados.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={S.mTitle}>{kit ? 'Editar Kit' : 'Novo Kit'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={S.row2}>
            <div style={S.fgroup}>
              <label style={S.label}>Geração (kWh/mês)</label>
              <input style={S.input} type="number" min="1" value={form.geracao} onChange={e => set('geracao', e.target.value)} placeholder="ex: 900" required />
            </div>
            <div style={S.fgroup}>
              <label style={S.label}>Potência Placa (Wp)</label>
              <input style={S.input} type="number" min="1" value={form.placas_w} onChange={e => set('placas_w', e.target.value)} placeholder="ex: 620" required />
            </div>
          </div>
          <div style={S.row2}>
            <div style={S.fgroup}>
              <label style={S.label}>Quantidade de Placas</label>
              <input style={S.input} type="number" min="1" value={form.quantidade} onChange={e => set('quantidade', e.target.value)} placeholder="ex: 10" required />
            </div>
            <div style={S.fgroup}>
              <label style={S.label}>Valor (R$)</label>
              <input style={S.input} type="text" value={form.valor} onChange={e => set('valor', e.target.value)} placeholder="ex: 14500.00" required />
            </div>
          </div>
          <div style={S.row2}>
            <div style={S.fgroup}>
              <label style={S.label}>Equipamento / Inversor</label>
              <input style={S.input} type="text" value={form.equipamento} onChange={e => set('equipamento', e.target.value)} placeholder="ex: SOLAX SHAK." required />
            </div>
            <div style={S.fgroup}>
              <label style={S.label}>Qtd Inversores</label>
              <input style={S.input} type="number" min="1" value={form.qtd_inversores} onChange={e => set('qtd_inversores', e.target.value)} placeholder="ex: 1" />
            </div>
          </div>
          <div style={S.row2}>
            <div style={S.fgroup}>
              <label style={S.label}>Marca</label>
              <input style={S.input} type="text" value={form.marca} onChange={e => set('marca', e.target.value)} placeholder="ex: Fronius" />
            </div>
            {(isChefe() || isGerenteComercial()) ? (
              <div style={S.fgroup}>
                <label style={S.label}>Garantia Inversor (anos)</label>
                <input style={S.input} type="number" min="1" value={form.garantia_inversor} onChange={e => set('garantia_inversor', e.target.value)} placeholder="ex: 15" />
              </div>
            ) : <div />}
          </div>
          {err && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{err}</div>}
          <div style={S.mFooter}>
            <button type="button" style={S.btnGhost} onClick={onClose}>Cancelar</button>
            <button type="submit" style={S.btnPrimary} disabled={saving}>{saving ? 'Salvando...' : (kit ? 'Salvar' : 'Criar Kit')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Página Principal ─── */
export default function Kits() {
  const { isChefe, isGerenteComercial } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const geracaoParam = queryParams.get('geracao') || '';

  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geracaoMin, setGeracaoMin] = useState(geracaoParam);
  const [geracaoMax, setGeracaoMax] = useState(geracaoParam);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);

  const fetchKits = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (geracaoMin) params.geracao_min = geracaoMin;
      if (geracaoMax) params.geracao_max = geracaoMax;
      const r = await api.get('/kits/', { params });
      setKits(r.data);
    } catch {
      setKits([]);
    } finally {
      setLoading(false);
    }
  }, [geracaoMin, geracaoMax]);

  useEffect(() => { fetchKits(); }, [fetchKits]);

  const handleSave = (saved, type) => {
    if (type === 'create') setKits(prev => [...prev, saved]);
    else setKits(prev => prev.map(k => k.id === saved.id ? saved : k));
    setModal(null);
  };

  const handleDelete = async () => {
    const kit = modal.kit;
    try {
      await api.delete(`/kits/${kit.id}/`);
      setKits(prev => prev.filter(k => k.id !== kit.id));
    } catch { /* silently */ }
    setModal(null);
  };

  /* filtro local por equipamento */
  const filtered = search.trim()
    ? kits.filter(k =>
        k.equipamento.toLowerCase().includes(search.toLowerCase()) ||
        (k.marca || '').toLowerCase().includes(search.toLowerCase())
      )
    : kits;

  const applySearch = (v) => setSearch(v);

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>Kits</div>
          <div style={S.sub}>{filtered.length} kit{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}</div>
        </div>
        {(isChefe() || isGerenteComercial()) && (
          <button style={S.btnPrimary} onClick={() => setModal({ type: 'create' })}>
            <Plus size={15} /> Novo Kit
          </button>
        )}
      </div>

      {/* Filtros */}
      <div style={S.filterBar}>
        <div>
          <div style={S.filterLabel}>Filtrar por Geração (kWh)</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input style={S.filterInput} type="number" min="0" placeholder="Mínimo" value={geracaoMin} onChange={e => setGeracaoMin(e.target.value)} />
            <span style={{ color: '#475569', fontSize: 13 }}>até</span>
            <input style={S.filterInput} type="number" min="0" placeholder="Máximo" value={geracaoMax} onChange={e => setGeracaoMax(e.target.value)} />
          </div>
        </div>

        <div>
          <div style={S.filterLabel}>Buscar Equipamento</div>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
            <input
              style={{ ...S.filterInput, width: 200, paddingLeft: 30 }}
              type="text"
              placeholder="ex: SOLAX, HUAWEI..."
              value={search}
              onChange={e => applySearch(e.target.value)}
            />
          </div>
        </div>

        {(geracaoMin || geracaoMax || search) && (
          <button style={{ ...S.btnGhost, alignSelf: 'flex-end' }} onClick={() => { setGeracaoMin(''); setGeracaoMax(''); applySearch(''); }}>
            <X size={13} /> Limpar
          </button>
        )}
      </div>

      {/* Tabela */}
      <div style={S.card}>
        {loading ? (
          <div style={S.empty}>Carregando kits...</div>
        ) : filtered.length === 0 ? (
          <div style={S.empty}>
            <Package size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
            <div>Nenhum kit encontrado.</div>
          </div>
        ) : (
          <table className="kits-table" style={S.table}>
            <thead>
              <tr>
                <th style={S.th(false)}>Geração</th>
                <th style={S.th(false)}>Wp</th>
                <th style={S.th(false)}>Qtd</th>
                <th style={S.th(false)}>Qtd Inversores</th>
                <th style={S.th(false)}>Equipamento</th>
                {(isChefe() || isGerenteComercial()) && <th style={S.th(false)}>Garantia Inversor</th>}
                <th style={S.th(false)}>Marca</th>
                <th style={S.th(true)}>Valor</th>
                {(isChefe() || isGerenteComercial()) && <th style={S.thLast}>Ações</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((k, i) => (
                <tr key={k.id} style={S.tr(i)}>
                  <td style={S.td(false)}>
                    <span className="kits-badge" style={S.badge}>{k.geracao} kWh</span>
                  </td>
                  <td style={S.td(false)}>{k.placas_w}</td>
                  <td style={S.td(false)}>{k.quantidade}</td>
                  <td style={S.td(false)}>{k.qtd_inversores ?? <span style={{ color: '#334155' }}>—</span>}</td>
                  <td style={{ ...S.td(false), fontWeight: 600, color: '#e2e8f0' }}>{k.equipamento}</td>
                  {(isChefe() || isGerenteComercial()) && <td style={S.td(false)}>{k.garantia_inversor != null ? `${k.garantia_inversor} anos` : <span style={{ color: '#334155' }}>—</span>}</td>}
                  <td style={S.td(false)}>{k.marca || <span style={{ color: '#334155' }}>—</span>}</td>
                  <td style={{ ...S.td(true), fontWeight: 700, color: '#4ade80' }}>{fmt(k.valor)}</td>
                  {(isChefe() || isGerenteComercial()) && (
                    <td style={S.tdLast}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button style={S.btnEdit} onClick={() => setModal({ type: 'edit', kit: k })}>
                          <Pencil size={12} />
                        </button>
                        <button style={S.btnDanger} onClick={() => setModal({ type: 'delete', kit: k })}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modais */}
      {modal?.type === 'create' && <KitModal onSave={handleSave} onClose={() => setModal(null)} />}
      {modal?.type === 'edit'   && <KitModal kit={modal.kit} onSave={handleSave} onClose={() => setModal(null)} />}
      {modal?.type === 'delete' && (
        <ConfirmModal
          msg={`Deletar o kit "${modal.kit.equipamento} – ${modal.kit.geracao} kWh"? Esta ação não pode ser desfeita.`}
          onConfirm={handleDelete}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
}
